import { useMemo } from "react";
import { Users, Clock } from "lucide-react";
import { cn } from "../../lib/utils";
import { timeToMinutes, minutesToTime, sessionStatus } from "../../lib/format";
import { STATUS_STYLES } from "../common";

const HOUR_PX = 64;

// Répartit les cours qui se chevauchent en colonnes côte à côte.
function layout(sessions) {
  const items = [...sessions]
    .map((s) => ({ s, start: timeToMinutes(s.heure_debut), end: timeToMinutes(s.heure_fin) }))
    .sort((a, b) => a.start - b.start || b.end - a.end);
  const groups = [];
  let current = [];
  let groupEnd = -1;
  items.forEach((it) => {
    if (it.start >= groupEnd && current.length) { groups.push(current); current = []; }
    current.push(it);
    groupEnd = Math.max(groupEnd, it.end);
  });
  if (current.length) groups.push(current);
  const out = [];
  groups.forEach((g) => {
    const cols = [];
    g.forEach((it) => {
      let c = cols.findIndex((end) => end <= it.start);
      if (c === -1) { c = cols.length; cols.push(it.end); } else cols[c] = it.end;
      it.col = c;
    });
    g.forEach((it) => { it.cols = cols.length; out.push(it); });
  });
  return out;
}

export function DayTimeline({ date, sessions = [], startHour = 7, endHour = 21, onSessionClick, onSlotClick, canAdd }) {
  const minH = Math.min(startHour, ...sessions.map((s) => Math.floor(timeToMinutes(s.heure_debut) / 60)));
  const maxH = Math.max(endHour, ...sessions.map((s) => Math.ceil(timeToMinutes(s.heure_fin) / 60)));
  const hours = [];
  for (let h = minH; h < maxH; h += 1) hours.push(h);
  const items = useMemo(() => layout(sessions), [sessions]);
  const status = sessionStatus(date);
  const now = new Date();
  const nowMin = now.getHours() * 60 + now.getMinutes();

  const handleGridClick = (e) => {
    if (!canAdd || !onSlotClick) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const minutes = minH * 60 + Math.floor((y / HOUR_PX) * 60 / 15) * 15;
    onSlotClick(minutesToTime(minutes));
  };

  return (
    <div className="relative flex" data-testid="day-timeline">
      <div className="w-14 shrink-0 select-none">
        {hours.map((h) => (
          <div key={h} style={{ height: HOUR_PX }} className="relative">
            <span className="absolute -top-2 right-2 text-[11px] tabular-nums text-muted-foreground">{String(h).padStart(2, "0")}:00</span>
          </div>
        ))}
      </div>
      <div
        className={cn("relative flex-1 border-l border-border", canAdd && "cursor-copy")}
        style={{ height: hours.length * HOUR_PX }}
        onClick={handleGridClick}
        title={canAdd ? "Cliquer pour ajouter un cours à cette heure" : undefined}
      >
        {hours.map((h, i) => (
          <div key={h} className="absolute left-0 right-0 border-t border-border" style={{ top: i * HOUR_PX }}>
            <div className="border-t border-dashed border-border/60" style={{ marginTop: HOUR_PX / 2 }} />
          </div>
        ))}
        {status === "aujourdhui" && nowMin >= minH * 60 && nowMin <= maxH * 60 && (
          <div className="absolute left-0 right-0 z-20 pointer-events-none" style={{ top: ((nowMin - minH * 60) / 60) * HOUR_PX }}>
            <div className="h-px bg-red-500" />
            <div className="absolute -left-1 -top-1 h-2 w-2 rounded-full bg-red-500" />
          </div>
        )}
        {items.map(({ s, start, end, col, cols }) => {
          const top = ((start - minH * 60) / 60) * HOUR_PX;
          const height = Math.max(((end - start) / 60) * HOUR_PX, 26);
          const width = 100 / cols;
          return (
            <button
              type="button"
              key={s.id}
              data-testid={`timeline-session-${s.id}`}
              onClick={(e) => { e.stopPropagation(); onSessionClick && onSessionClick(s); }}
              className={cn(
                "absolute z-10 rounded-md border border-border border-l-4 px-2 py-1 text-left shadow-sm overflow-hidden hover:shadow-md hover:z-30 transition-shadow",
                STATUS_STYLES[status].block,
              )}
              style={{ top: top + 1, height: height - 2, left: `calc(${col * width}% + 4px)`, width: `calc(${width}% - 8px)` }}
            >
              <p className="text-sm font-semibold leading-tight truncate">{s.module_nom}</p>
              <p className="text-[11px] text-muted-foreground flex items-center gap-1 tabular-nums"><Clock size={11} />{s.heure_debut} – {s.heure_fin}{s.salle ? ` · ${s.salle}` : ""}</p>
              {height > 52 && (
                <p className="text-[11px] text-muted-foreground flex items-center gap-1 mt-0.5 truncate">
                  <Users size={11} className="shrink-0" />
                  {s.eleves?.length ? s.eleves.map((e) => e.prenom).join(", ") : "Aucun élève"}
                </p>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
